import { Component } from 'preact';
import { GOLD, RADIUS } from '../../lib/medievalTheme';
import { t } from '../../localization/t';

const THUMB_WIDTH = '4.5cqw';
const STACK_OFFSET = '1.4cqw';

const FRAME_STYLE = {
  border: `1px solid ${GOLD} 0.55)`,
  boxShadow: '0 6px 16px rgba(0,0,0,0.55), 0 0 12px rgba(212,168,67,0.18), inset 0 1px 0 rgba(255,255,255,0.08)',
  background: 'rgba(12,10,8,0.85)',
  overflow: 'hidden',
};

function siteAspect() {
  return 'calc(4.5cqw * 63.5 / 88.9)';
}
function portraitAspect() {
  return 'calc(4.5cqw * 88.9 / 63.5)';
}

/**
 * Side-mounted full-thumbnail stack rendered next to whichever card is
 * currently hovered. Thumbnail layout is exactly the original spec.
 * 4.5cqw width, 1.4cqw stack offset via negative top margins, 0.4cqw
 * gold border, alternating ±1.2° tilt. So the hover view is visually
 * identical to the pre-peek implementation.
 *
 * The outer container is always rendered (no display toggle); position
 * is anchored each frame to the host card's top-left by
 * GameBoard.updateHoverStatusOverlayPosition. The inner
 * `.hover-equipped-slide` wrapper carries the entry/exit transition.
 * it slides in from upper-right (the peek position) and fades, then
 * reverses on hover end.
 *
 * `lastList` persists the previous non-empty equipped-cards list so the
 * fade-out animation has the same children to render while the wrapper
 * transitions to opacity 0. Without that, hover-end would unmount the
 * children immediately and the wrapper would fade empty.
 */
// Amber. Matches the in-play owner-unit glow + the open unit-hand badge.
const AMBER = '#ffbf40';

export default class HoverEquippedCards extends Component {
  lastList = [];
  lastCasters = [];

  render({ hoverRef, equippedCards, casterIds }) {
    const list = equippedCards || [];
    const visible = list.length > 0;
    if (visible) { this.lastList = list; this.lastCasters = casterIds || []; }
    const renderList = visible ? list : this.lastList;
    // An equipped card that owns a unit hand is a "caster" hidden on its
    // bearer. Highlight it amber so the player can still locate it.
    const casters = new Set(visible ? (casterIds || []) : this.lastCasters);
    return (
      <div
        ref={hoverRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          pointerEvents: 'none',
          zIndex: 49,
          left: 0,
          top: 0,
          willChange: 'transform',
        }}
      >
        <div
          className="hover-equipped-slide"
          data-visible={visible ? 'true' : 'false'}
        >
          {renderList.map((eq, i) => {
            const isSite = !!eq.isSite;
            const height = isSite ? siteAspect() : portraitAspect();
            const isCaster = casters.has(eq.id);
            return (
              <div
                key={eq.id}
                style={{
                  width: THUMB_WIDTH,
                  height,
                  marginTop: i === 0 ? 0 : `calc(-1 * (${height} - ${STACK_OFFSET}))`,
                  transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 1.2}deg)`,
                  ...FRAME_STYLE,
                  borderRadius: isSite ? RADIUS.cardLandscape : RADIUS.card,
                  ...(isCaster ? {
                    border: `2px solid ${AMBER}`,
                    boxShadow: `0 6px 16px rgba(0,0,0,0.55), 0 0 14px ${AMBER}`,
                  } : {}),
                }}
                title={isCaster ? t('{name} (caster)', { name: eq.name }) : eq.name}
              >
                <img
                  src={eq.imageUrl || ''}
                  alt={eq.name || ''}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
